import React, { useCallback, useState } from "react";
import { Box, Tab, Tabs, Typography } from "@mui/material";
import { useAdminLanguage } from "../../../context/AdminLanguageContext";
import PuzzleBoardInventoryPanel from "../../pzl_common/stats/PuzzleBoardInventoryPanel";
import PuzzlePlayUserStatsPanel from "../../pzl_common/stats/PuzzlePlayUserStatsPanel";
import StatsSlidingService from "../../../services/StatsSlidingService";
import SlidingTagStatsPanel from "./SlidingTagStatsPanel";
import SlidingTopPuzzlesPanel from "./SlidingTopPuzzlesPanel";
import SlidingTopUsersPanel from "./SlidingTopUsersPanel";
import SlidingPromotionPanel from "./SlidingPromotionPanel";

const TAB_USERS = 0;
const TAB_TOP_USERS = 1;
const TAB_TOP_PUZZLES = 2;
const TAB_PUZZLES = 3;
const TAB_TAGS = 4;
const TAB_PROMOTION = 5;
const VALID_TABS = [
    TAB_USERS,
    TAB_TOP_USERS,
    TAB_TOP_PUZZLES,
    TAB_PUZZLES,
    TAB_TAGS,
    TAB_PROMOTION,
];

const RANGE_STORAGE_KEY = "admin.sliding.userStats.range";
const TAB_STORAGE_KEY = "admin.sliding.stats.tab";

function loadStoredTab() {
    try {
        const v = parseInt(window.localStorage.getItem(TAB_STORAGE_KEY), 10);
        if (VALID_TABS.includes(v)) return v;
    } catch {}
    return TAB_USERS;
}

export default function AdminSlidingStatsMain() {
    const { language } = useAdminLanguage();
    const [activeTab, setActiveTab] = useState(loadStoredTab);
    // A panel loads its stats on mount, so mount it on first visit and keep it mounted after that.
    const [visitedTabs, setVisitedTabs] = useState(() => [loadStoredTab()]);

    const handleTabChange = (event, value) => {
        setActiveTab(value);
        setVisitedTabs((tabs) => (tabs.includes(value) ? tabs : [...tabs, value]));
        try {
            window.localStorage.setItem(TAB_STORAGE_KEY, String(value));
        } catch {}
    };

    const fetchUserPlays = useCallback((params) => StatsSlidingService.userPlayStats(params), []);
    const fetchByDifficulty = useCallback((lang) => StatsSlidingService.statsByDifficulty(lang), []);

    const tabPanelSx = (tab) => ({
        display: activeTab === tab ? "flex" : "none",
        flexDirection: "column",
        flex: 1,
        minHeight: 0,
        overflow: "auto",
    });

    return (
        <Box
            sx={{
                p: 2,
                minHeight: "100%",
                boxSizing: "border-box",
                display: "flex",
                flexDirection: "column",
            }}
        >
            <Typography variant="h5" sx={{ fontWeight: 600, color: "primary.main", mb: 1, flexShrink: 0 }}>
                Sliding Statistics
            </Typography>

            <Box sx={{ borderBottom: 1, borderColor: "divider", mb: 2, flexShrink: 0 }}>
                <Tabs value={activeTab} onChange={handleTabChange} aria-label="Sliding statistics sections">
                    <Tab
                        label="Users"
                        id="sliding-stats-tab-0"
                        aria-controls="sliding-stats-tabpanel-0"
                        sx={{ textTransform: "none", fontWeight: 600, minHeight: 48 }}
                    />
                    <Tab
                        label="Top users"
                        id="sliding-stats-tab-1"
                        aria-controls="sliding-stats-tabpanel-1"
                        sx={{ textTransform: "none", fontWeight: 600, minHeight: 48 }}
                    />
                    <Tab
                        label="Top puzzles"
                        id="sliding-stats-tab-2"
                        aria-controls="sliding-stats-tabpanel-2"
                        sx={{ textTransform: "none", fontWeight: 600, minHeight: 48 }}
                    />
                    <Tab
                        label="Puzzles"
                        id="sliding-stats-tab-3"
                        aria-controls="sliding-stats-tabpanel-3"
                        sx={{ textTransform: "none", fontWeight: 600, minHeight: 48 }}
                    />
                    <Tab
                        label="Tags"
                        id="sliding-stats-tab-4"
                        aria-controls="sliding-stats-tabpanel-4"
                        sx={{ textTransform: "none", fontWeight: 600, minHeight: 48 }}
                    />
                    <Tab
                        label="Promotion"
                        id="sliding-stats-tab-5"
                        aria-controls="sliding-stats-tabpanel-5"
                        sx={{ textTransform: "none", fontWeight: 600, minHeight: 48 }}
                    />
                </Tabs>
            </Box>

            {/* Panels stay mounted while hidden so switching tabs does not reload their stats */}
            <Box
                role="tabpanel"
                hidden={activeTab !== TAB_USERS}
                id="sliding-stats-tabpanel-0"
                aria-labelledby="sliding-stats-tab-0"
                sx={tabPanelSx(TAB_USERS)}
            >
                {visitedTabs.includes(TAB_USERS) && (
                    <PuzzlePlayUserStatsPanel
                        language={language}
                        fetchStats={fetchUserPlays}
                        rangeStorageKey={RANGE_STORAGE_KEY}
                        difficultyChartTitle="Grid sizes played"
                        difficultyChartHint="Plays for 4×4 and 6×6, each split into Easy, Medium, and Hard."
                        notificationAppLabel="Sliding Puzzle Quest"
                        csvFilePrefix="sliding"
                    />
                )}
            </Box>

            <Box
                role="tabpanel"
                hidden={activeTab !== TAB_TOP_USERS}
                id="sliding-stats-tabpanel-1"
                aria-labelledby="sliding-stats-tab-1"
                sx={tabPanelSx(TAB_TOP_USERS)}
            >
                {visitedTabs.includes(TAB_TOP_USERS) && <SlidingTopUsersPanel language={language} />}
            </Box>

            <Box
                role="tabpanel"
                hidden={activeTab !== TAB_TOP_PUZZLES}
                id="sliding-stats-tabpanel-2"
                aria-labelledby="sliding-stats-tab-2"
                sx={tabPanelSx(TAB_TOP_PUZZLES)}
            >
                {visitedTabs.includes(TAB_TOP_PUZZLES) && <SlidingTopPuzzlesPanel language={language} />}
            </Box>

            <Box
                role="tabpanel"
                hidden={activeTab !== TAB_PUZZLES}
                id="sliding-stats-tabpanel-3"
                aria-labelledby="sliding-stats-tab-3"
                sx={tabPanelSx(TAB_PUZZLES)}
            >
                {visitedTabs.includes(TAB_PUZZLES) && (
                    <PuzzleBoardInventoryPanel
                        language={language}
                        fetchStats={fetchByDifficulty}
                        sizeColumnLabel="Grid / difficulty"
                    />
                )}
            </Box>

            <Box
                role="tabpanel"
                hidden={activeTab !== TAB_TAGS}
                id="sliding-stats-tabpanel-4"
                aria-labelledby="sliding-stats-tab-4"
                sx={tabPanelSx(TAB_TAGS)}
            >
                {visitedTabs.includes(TAB_TAGS) && (
                    <SlidingTagStatsPanel language={language} />
                )}
            </Box>

            <Box
                role="tabpanel"
                hidden={activeTab !== TAB_PROMOTION}
                id="sliding-stats-tabpanel-5"
                aria-labelledby="sliding-stats-tab-5"
                sx={tabPanelSx(TAB_PROMOTION)}
            >
                {visitedTabs.includes(TAB_PROMOTION) && <SlidingPromotionPanel language={language} />}
            </Box>
        </Box>
    );
}
